import type { FormikHelpers } from 'formik';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { CloudUploadScope } from 'api/apiCloudflare';
import * as apiProduct from 'api/apiProducts';
import { useAuth } from 'context/AuthContext';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { FilesContainer } from 'modules/files';
import { parseCloudFiles, stringifyCloudFiles } from 'modules/files';
import type { ProductEditType } from '../../types';

interface ProductFormFilesProps {
    setFieldValue: FormikHelpers<ProductEditType>['setFieldValue'];
    values: ProductEditType;
    ensureSaved: () => Promise<CloudUploadScope | false>;
}

const ProductFormFiles = ({ setFieldValue, values, ensureSaved }: ProductFormFilesProps) => {
    const { auth } = useAuth();
    const { t } = useTranslation(['Products', 'FileNavigator', 'common']);
    const productId = Number(values?.ID || 0);
    const productIdRef = useRef(productId);
    const canEdit = hasUserAccess(auth?.user, 'prodEdit');
    const files = parseCloudFiles(values.prodFiles);

    useEffect(() => {
        productIdRef.current = productId;
    }, [productId]);

    const handleFilesChange = async (nextFiles: ReturnType<typeof parseCloudFiles>) => {
        const nextJson = stringifyCloudFiles(nextFiles);
        const currentId = productIdRef.current;
        if (currentId <= 0) {
            setFieldValue('prodFiles', nextJson);
            return true;
        }
        const stat = await apiProduct.saveFiles(currentId, nextJson);
        if (!stat) return false;
        setFieldValue('prodFiles', nextJson);
        return true;
    };

    const handleEnsureEntity = async (): Promise<CloudUploadScope | false> => {
        const scope = await ensureSaved();
        if (!scope) return false;
        const createdId = Number(scope.productId || 0);
        if (createdId > 0) productIdRef.current = createdId;
        return scope;
    };

    return (
        <FilesContainer
            files={files}
            scope={{ productId }}
            canEdit={canEdit}
            addButtonLabel={t('btnAddFiles')}
            uploadingLabel={t('common:buttons.uploading')}
            saveFirstMessage={t('messSaveProductFirst')}
            errorMessage={t('common:messError')}
            uploadSuccessMessage={t('FileNavigator:messDownloadSuccess')}
            onFilesChange={handleFilesChange}
            onEnsureEntity={productId <= 0 ? handleEnsureEntity : undefined}
        />
    );
};

export default ProductFormFiles;
